if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

/* BSG: quantities for standard quick-add cards with a single variant. */
if (!customElements.get('bsg-card-quantity')) {
  const bsgRoot = () => window.Shopify?.routes?.root || '/';
  let bsgQueue = Promise.resolve();
  let bsgRefreshTimer;
  let bsgReadVersion = 0;
  function bsgPaint(cart) {
    document.querySelectorAll('bsg-card-quantity').forEach((control) => control.paint(cart));
  }
  async function bsgRead() {
    const version = ++bsgReadVersion;
    const response = await fetch(bsgRoot() + 'cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to read cart');
    const cart = await response.json();
    if (version === bsgReadVersion) bsgPaint(cart);
    return cart;
  }
  function bsgRefresh() {
    clearTimeout(bsgRefreshTimer);
    bsgRefreshTimer = setTimeout(() => bsgRead().catch(console.error), 80);
  }
  customElements.define('bsg-card-quantity', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.style.cssText = 'display:block;position:relative;z-index:2';
      this.original = this.parentElement.querySelector('product-form');
      this.originalDisplay = this.original?.style.display || '';
      this.controls = document.createElement('div');
      this.controls.style.cssText = 'display:none;align-items:center;justify-content:space-between;gap:8px;min-height:44px;border:1px solid currentColor;border-radius:8px';
      this.minus = this.makeButton('−', this.dataset.minus, -1);
      this.value = document.createElement('span');
      this.value.setAttribute('role', 'status');
      this.value.setAttribute('aria-live', 'polite');
      this.plus = this.makeButton('+', this.dataset.plus, 1);
      this.controls.append(this.minus, this.value, this.plus);
      this.errorBox = document.createElement('p');
      this.errorBox.setAttribute('role', 'alert');
      this.errorBox.style.cssText = 'font-size:12px;margin:4px 0';
      this.append(this.controls, this.errorBox);
      bsgRefresh();
    }
    makeButton(text, label, delta) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = text;
      button.setAttribute('aria-label', label);
      button.style.cssText = 'min-width:44px;min-height:44px;background:transparent;color:inherit;border:0;font:inherit;font-size:22px;cursor:pointer';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.busy) return;
        this.busy = true;
        this.minus.disabled = this.plus.disabled = true;
        bsgQueue = bsgQueue.then(() => this.change(delta)).catch(console.error).finally(() => {
          this.busy = false;
          this.minus.disabled = this.plus.disabled = false;
        });
      });
      return button;
    }
    paint(cart) {
      const lines = cart.items.filter((line) => String(line.variant_id) === this.dataset.variant);
      // Do not change subscriptions, personalized items or ambiguous split lines.
      this.line = lines.length === 1 && !lines[0].selling_plan_allocation && !Object.keys(lines[0].properties || {}).length ? lines[0] : null;
      const visible = Boolean(this.line && this.line.quantity > 0);
      this.controls.style.display = visible ? 'flex' : 'none';
      if (this.original) this.original.style.display = visible ? 'none' : this.originalDisplay;
      this.value.textContent = visible ? String(this.line.quantity) : '';
    }
    async change(delta) {
      this.errorBox.textContent = '';
      try {
        const cart = await bsgRead();
        const lines = cart.items.filter((line) => String(line.variant_id) === this.dataset.variant);
        if (lines.length !== 1 || !this.line) return;
        const line = this.line;
        const drawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const sections = drawer?.getSectionsToRender().map((section) => section.id) || [];
        const response = await fetch(bsgRoot() + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: Math.max(0, line.quantity + delta), sections, sections_url: location.pathname })
        });
        const updated = await response.json();
        if (!response.ok || updated.status) throw new Error(updated.description || updated.message || 'Unable to update cart');
        ++bsgReadVersion;
        bsgPaint(updated);
        if (drawer && updated.sections) {
          drawer.classList.toggle('is-empty', updated.item_count === 0);
          drawer.setActiveElement(this.plus);
          drawer.renderContents(updated);
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bsg-card-quantity', productVariantId: this.dataset.variant, cartData: updated });
        }
      } catch (error) {
        this.errorBox.textContent = error.message;
        await bsgRead().catch(console.error);
      }
    }
  });
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, bsgRefresh);
  }
  window.addEventListener('pageshow', bsgRefresh);
  window.addEventListener('focus', bsgRefresh);
}